import { XYCoord } from 'react-dnd';
import { ILayout, ILayoutComponent, emptyId, isEmptyId } from '@diana/core';
import { isSectionComponent } from '../../../Support';

export const getDistanceToElement = (x: number, y: number, element: Element) => {
  // Get the element's bounding rectangle
  const rect = element.getBoundingClientRect();
  return { rect, distance: getDistance(x, y, rect) };
};

export const getDistance = (
  x: number,
  y: number,
  rect: Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>,
) => {
  // Calculate the distance to each edge of the element
  const dx = Math.max(rect.left - x, 0, x - rect.right);
  const dy = Math.max(rect.top - y, 0, y - rect.bottom);

  // If the point is inside the rectangle, one of dx or dy will be zero
  return Math.sqrt(dx * dx + dy * dy);
};

export const parseWidthSize = (width?: string | null) => {
  const size = Number((width || 'col-12').replace(/^col-/, ''));
  return size > 0 && size <= 12 ? size : 12;
};

export const getSectionGridHtmlElement = (section: ILayoutComponent) => {
  const elements = Array.from(
    document.querySelectorAll(`[data-layout-component-id="${section.id}"] .MuiGrid-container`),
  );

  for (const element of elements) {
    if (element.querySelector('.MuiGrid-item[data-grid-layout-component-id]')) {
      return element;
    }
  }

  return elements[elements.length - 1] ?? null;
};

export const getPlaceholderDistance = (
  grid: Element,
  items: ILayoutComponent[],
  dragItem: ILayoutComponent,
  clientOffset: XYCoord,
) => {
  const lastItem = [...items]
    .reverse()
    .map((item): [ILayoutComponent, Element] | null => {
      if (dragItem.id !== item.id) {
        const element = grid.querySelector(
          `.MuiGrid-item[data-grid-layout-component-id="${item.id}"]`,
        );
        if (element) return [item, element];
      }
      return null;
    })
    .find(Boolean);

  if (!lastItem) return getDistanceToElement(clientOffset.x, clientOffset.y, grid);

  const rect = lastItem[1].getBoundingClientRect();
  const gridRect = grid.getBoundingClientRect();

  let occupiedSpace = 0;
  for (const item of items) {
    const size = parseWidthSize(item.properties.width);
    occupiedSpace += size;
    if (occupiedSpace > 12) occupiedSpace = size;
    if (item.id === lastItem[0].id) break;
  }

  const isPlacedOnTheRightSideOfLastItem =
    occupiedSpace + parseWidthSize(dragItem.properties.width) <= 12;

  const placeholderRect = {
    top: isPlacedOnTheRightSideOfLastItem ? rect.top : rect.bottom,
    right: gridRect.right,
    bottom: isPlacedOnTheRightSideOfLastItem ? rect.bottom : gridRect.bottom,
    left: isPlacedOnTheRightSideOfLastItem ? rect.right : gridRect.left,
  };

  return {
    rect: placeholderRect,
    distance: getDistance(clientOffset.x, clientOffset.y, placeholderRect),
  };
};

export const getPlaceholdersInLayout = (
  layout: ILayout,
  dragItem: ILayoutComponent,
  clientOffset: XYCoord,
) => {
  const result: {
    distance: number;
    index: number;
    parentId: string;
    isSibling: boolean;
  }[] = [];

  const grids: {
    element: Element;
    items: ILayoutComponent[];
    index: number;
    parentId: string;
  }[] = [];

  const sections = layout.components.filter((c) => isEmptyId(c.parentId));

  // get all level 1 grid
  if (isSectionComponent(dragItem)) {
    const element = document.querySelector(`[data-layout-id="${layout.id}"] .MuiGrid-container`);
    if (element) {
      grids.push({
        element,
        items: sections,
        parentId: emptyId,
        index: sections.length
          ? layout.components.indexOf(sections[sections.length - 1]) + 1
          : layout.components.length,
      });
    }
  }

  // get all level 2 grid
  if (!isSectionComponent(dragItem)) {
    for (const section of sections) {
      if (isSectionComponent(section)) {
        const items = layout.components.filter((c) => c.parentId === section.id);
        const element = getSectionGridHtmlElement(section);
        if (element) {
          grids.push({
            element,
            items,
            parentId: section.id,
            index: items.length
              ? layout.components.indexOf(items[items.length - 1]) + 1
              : layout.components.indexOf(section) + 1,
          });
        }
      }
    }
  }

  // identify placeholders
  for (const { element: grid, items, index, parentId } of grids) {
    const { rect, distance } = getPlaceholderDistance(grid, items, dragItem, clientOffset);
    if (rect.bottom < 25) continue;
    result.push({
      distance,
      index,
      parentId,
      isSibling: items.some((lc) => lc.id === dragItem.id),
    });
  }

  return result;
};
